import React from 'react';

import {Text} from '@albert-io/atomic';
import classnames from 'classnames';

interface Props {
  cardClassName?: string;
  iconContent: React.ReactNode;
  iconClassName: string;
  title: string;
  description?: string;
  horizontal?: boolean;
  /** Trailing affordance (horizontal only) — e.g. the Clever re-sync icon. */
  trailing?: React.ReactNode;
  onClick: () => void;
  /**
   * Marks an in-progress action (e.g. a running sync). The card stays focusable — unlike `disabled`,
   * which drops focus to the body — and `aria-busy` announces the state; clicks are ignored while busy.
   */
  busy?: boolean;
  'data-testid'?: string;
}

export const ActionCard = ({
  cardClassName,
  iconContent,
  iconClassName,
  title,
  description,
  horizontal = false,
  trailing,
  onClick,
  busy = false,
  'data-testid': testId
}: Props) => (
  <button
    aria-busy={busy || undefined}
    className={classnames(
      cardClassName,
      'flex w-full min-w-x20 rounded-x1-half border border-border-primary background-primary text-left focus-ring',
      {
        'items-center gap-x2 p-x1-half': horizontal,
        'h-full flex-col items-start justify-between gap-x2 p-x3': !horizontal,
        'cursor-default': busy,
        'cursor-pointer hover:background-primary-hover active:background-primary-pressed': !busy
      }
    )}
    data-testid={testId}
    onClick={() => {
      if (!busy) {
        onClick();
      }
    }}
    type='button'
  >
    <span
      className={classnames('flex shrink-0 items-center justify-center', iconClassName, {
        'h-x4 w-x4 rounded-full text-m': horizontal,
        'h-x5 w-x5 rounded-x1 text-xl': !horizontal
      })}
    >
      {iconContent}
    </span>
    <div
      className={classnames('flex flex-col gap-x0-quarter', {
        'min-w-0 grow': horizontal
      })}
    >
      <Text color='primary' size={horizontal ? 's' : 'regular'}>
        {title}
      </Text>
      {description ? (
        <Text color='tertiary' size='s'>
          {description}
        </Text>
      ) : null}
    </div>
    {horizontal ? trailing : null}
  </button>
);
